<script lang="ts">
  import type { MessageGroup, MessageGroupMessage } from "@shared/types/MessageGroup";
  import type { PaginationDetails } from "@shared/types/Pagination";
  import NewMessageModal from "../Components/NewMessageModal/Main.svelte";
  import LeftPane from "./LeftPane.svelte";
  import Conversation from "../Components/Conversation.svelte";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";
  import formatPhone from "@shared/filters/phone.js";
  import {
    Http,
    HttpPromiseWithEntireResponse,
    HttpPromise,
  } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import * as _ from "lodash";
  import messagesContext from "@modern/services/messages_context";
  import { goto } from "@roxi/routify";
  import { onDestroy, onMount } from "svelte";

  export let messagesRes: any;
  export let connectedReps: any;
  let conversations: MessageGroup[] = messagesRes;
  let currentConversation: MessageGroup = conversations[0];
  let currentMessages: MessageGroupMessage[] = [];
  export let searchQuery: string = "";
  export let page = 1;
  export let salesRepId: number = null;

  let errors = { messages: [] };

  let smsPhoneNumber = null;
  function getSmsPhoneNumber() {
    if (currentConversation && currentConversation.last_message.sms_number) {
      smsPhoneNumber = formatPhone(conversations[0].last_message.sms_number);
    }
  }
  getSmsPhoneNumber();

  async function refreshConversations(params = {}) {
    $goto("/messaging", params);
    conversations = [];
    let res = await HttpPromiseWithEntireResponse({
      method: "GET",
      url: "/api/retailer/message_groups",
      data: params,
    });
    if (res.ok) {
      conversations = await res.json();
      currentConversation = conversations[0];
      currentMessages = [];
      init();
    }
  }

  let pagination: PaginationDetails = {
    perPage: parseInt(messagesRes.headers.get("X-Pagination-Limit")),
    page: page,
    totalEntries: messagesRes.headers.get("X-Pagination-Entries"),
    goToPage: function (n) {
      refreshConversations({ page: n });
    },
    loadMore: function () {
      loadMoreMessages();
    },
  };

  async function loadMoreMessages() {
    let totalEntries = pagination.totalEntries,
      totalPages = Math.ceil(totalEntries / pagination.perPage);

    if (page === totalPages) {
      return;
    }
    let newPage = page + 1;
    page = pagination.page = newPage;

    let res = await HttpPromiseWithEntireResponse({
      method: "GET",
      url: "/api/retailer/message_groups",
      data: { page: newPage },
    });
    if (res.ok) {
      let newData = await res.json();
      // set new value for total entries
      pagination.totalEntries = Number(res.headers.get("X-Pagination-Entries"));

      // append data to conversations
      if (newData.length && conversations.length < totalEntries) {
        conversations = conversations.concat(newData);
      }
    }
  }

  var postFetch = function (convo) {
    conversations.unshift(convo);
    conversations = conversations;
    changeConversation(convo.sales_reps.map(x => x.id));
  };

  function createConvo(salesRepId) {
    Http(
      {
        method: "POST",
        url: "/api/retailer/message_groups",
        data: { message_group: { sales_rep_id: salesRepId } },
      },
      function (res) {
        const newConvo = res;
        newConvo.newMessage = true;
        postFetch(newConvo);
      },
      function (res) {
        errors = res;
        currentConversation = findCurrentConvo(salesRepId);
      },
    );
  }

  function fetchConvo(salesRepId) {
    Http(
      {
        method: "GET",
        url: "/api/retailer/message_groups",
        data: { sales_rep_id: salesRepId },
      },
      function (res) {
        var convo = res[0];
        if (convo == null) {
          createConvo(salesRepId);
        } else {
          postFetch(convo);
        }
      },
      function (err) {
        console.log("fetch error", err);
        Flash.now({
          type: "error",
          message: "Sorry - we had a problem. Please reload the page or contact support.",
          header: "Error",
        });
      },
    );
  }

  function findCurrentConvo(salesRepId) {
    if (!salesRepId) {
      return null;
    }
    return conversations.find(function (x) {
      return x.sales_reps.find(function (y) {
        return y.id == salesRepId;
      });
    });
  }

  function doScroll() {
    setTimeout(function () {
      let scrollEl = document.getElementById("message-window-scroller");
      if (scrollEl) {
        scrollEl.scrollIntoView({ block: "end", inline: "end" });
      }
    }, 200);
  }

  function changeConversation(salesRepIds) {
    currentConversation = findCurrentConvo(salesRepIds[0]);
    if (currentConversation == null) {
      fetchConvo(salesRepIds[0]);
      return;
    }

    Http(
      {
        method: "GET",
        url: "/api/retailer/messages",
        data: {
          sales_rep_ids: salesRepIds,
        },
      },
      function (res) {
        currentMessages = res;
        if (!currentConversation.last_message) {
          currentConversation.last_message = res[res.length - 1];
          currentConversation = currentConversation;
        }
        updateRetailerSeen(currentConversation);
        doScroll();
      },
      function error(res) {
        errors = res;
      },
    );
  }

  function init() {
    if (salesRepId) {
      changeConversation([salesRepId]);
    } else if (currentConversation) {
      changeConversation(currentConversation.sales_reps.map(x => x.id));
    }
  }
  init();

  function updateConvo(convo, doNotSetCurrent) {
    let i = conversations.findIndex(function (x) {
      return x.sales_reps.find(function (y) {
        return convo.sales_reps.map(x => x.id).includes(y.id);
      });
    });
    if (i < 0) {
      return;
    }
    conversations = conversations.slice();
    conversations[i] = convo;
    if (doNotSetCurrent) {
      return;
    }
    currentConversation = findCurrentConvo(convo.sales_rep.id);
  }

  function updateRetailerSeen(conversation) {
    if (!((conversation || {}).distributors || [])[0]) {
      return;
    }

    conversation.sales_reps.forEach(function (s) {
      Http(
        {
          method: "PUT",
          url: "/api/retailer/message_groups/update_retailer_seen",
          data: {
            sales_rep_id: s.id,
          },
        },
        function (res) {
          conversation.num_unseen = 0;
          (conversation.last_message || {}).seen = true;
          updateConvo(conversation, true);
        },
        function (res) {
          errors = res;
        },
      );
    });
  }

  let showNewMessageModal = false;
  function openNewMessageModal() {
    showNewMessageModal = true;
  }

  let hasSelectedAConversation = false;

  function changeConversationWrapper(convo) {
    hasSelectedAConversation = true;
    changeConversation(convo.sales_reps.map(x => x.id));
  }

  function goBackToPane() {
    hasSelectedAConversation = false;
  }

  const forceIndex = new URLSearchParams(window.location.search).get("index");
  $: hasSelectedAConversation = !!currentConversation && !forceIndex;
  let convosWithSalesReps = conversations.map(convo => convo.sales_reps.length > 0);
  let hasSalesReps = connectedReps.has_connected_reps || convosWithSalesReps.length;

  function updateMessage(message) {
    let i = currentMessages.findIndex(function (x) {
      return x.id == message.id;
    });
    if (i < 0) {
      return;
    }
    currentMessages = currentMessages.slice();
    currentMessages[i] = message;
  }

  async function respond(params) {
    await HttpPromise({
      method: "POST",
      url: "/api/retailer/messages/create_in_response",
      data: {
        message: params,
      },
    })
      .then(res => {
        currentConversation.last_message = res;
        changeConversation([params.sales_rep_id]);
      })
      .catch(res => {
        errors = res;
      });
  }

  function message(params) {
    Http(
      {
        method: "POST",
        url: "/api/retailer/messages",
        data: {
          message: params,
        },
      },
      function (res) {
        changeConversation([params.sales_rep_id]);
      },
      function (res) {
        errors = res;
      },
    );
  }

  function search(query) {
    let params = {};
    if (query != "" || query != null) {
      params = { search: query, page: 1 };
    }
    refreshConversations(params);
  }

  function changeSeen(conversation, seen) {
    if (seen) {
      return updateRetailerSeen(conversation);
    }
    _.each(conversation.sales_reps, function (s) {
      Http(
        {
          method: "PUT",
          url: "/api/retailer/message_groups/mark_unseen",
          data: {
            sales_rep_id: s.id,
          },
        },
        function (res) {
          conversation.num_unseen = 1;
          (conversation.last_message || {}).seen = false;
          updateConvo(conversation, true);
        },
        function (res) {
          errors = res;
        },
      );
    });
  }

  function viewOrders(conversation) {
    return window["stateService"].href("layout.order_groups", {
      distributors: [conversation.distributors[0].id],
    });
  }

  function updateLeftPaneCount(message) {
    let otherConvo = findCurrentConvo(message.sales_rep_id);
    if (!otherConvo) {
      return;
    }
    if (!otherConvo.num_unseen) {
      otherConvo.num_unseen = 0;
    }
    otherConvo.num_unseen += 1;
    otherConvo.last_message = message;
    updateConvo(otherConvo, true);
  }

  let callbacks = {
    changeConversation: changeConversation,
    createConvo: createConvo,
    search: search,
    respond: respond,
    message: message,
    changeSeen: changeSeen,
    viewOrders: viewOrders,
  };

  let unsubscribeNewMessageStore = () => {};
  let unsubscribeUpdatedMessageStore = () => {};
  if (messagesContext.getNewMessageStore()) {
    unsubscribeNewMessageStore = messagesContext.getNewMessageStore().subscribe(d => {
      if (!(d && d.new_message)) {
        return;
      }
      if (!currentConversation) {
        return;
      }

      if (!_.includes(currentConversation.ids, d.new_message.message_group_id)) {
        return updateLeftPaneCount(d.new_message);
      }
      if (
        _.find(currentMessages, function (cm) {
          return cm.id == d.new_message.id;
        })
      ) {
        return;
      }
      currentMessages = _.concat(currentMessages, [d.new_message]);
    });

    unsubscribeUpdatedMessageStore = messagesContext.getUpdatedMessageStore().subscribe(d => {
      if (!(d && d.updated_message)) {
        return;
      }
      if (!currentConversation) {
        return;
      }
      if (!_.includes(currentConversation.ids, d.updated_message.message_group_id)) {
        return;
      }
      updateMessage(d.updated_message);
    });
  }

  onMount(() => {
    HttpPromise({
      method: "GET",
      url: "/api/retailer/sales_reps/prime_assigned_distributors_cache",
    });
  });

  onDestroy(() => {
    unsubscribeNewMessageStore();
    unsubscribeUpdatedMessageStore();
  });
</script>

<FormErrors {errors} />
<div class="grid-container padding--small">
  <div class="grid-row fizz-flex-row">
    <div class={`grid-col c12-12 c4-12--lg ${hasSelectedAConversation ? "show--md" : ""}`}>
      <LeftPane
        {searchQuery}
        {callbacks}
        {conversations}
        {currentConversation}
        {pagination}
        {openNewMessageModal}
        {changeConversationWrapper}
        {smsPhoneNumber}
        {hasSalesReps}
      />
    </div>

    <div
      class={`grid-col c12-12 c8-12--lg ${!hasSelectedAConversation ? "show--md" : ""} fizz-styles`}
    >
      {#if !hasSalesReps}
        <div
          class="grid-container-body padding-top--smallest margin-bottom--small fizz-full-height"
        >
          <div class="grid-row align-center">
            <span
              class="grid-col empty-messages inline-flex flex-column"
              style="justify-content: center; padding-top: unset;"
            >
              <div class="fizz-heading-4">Messages is not available</div>
              <br />
              <div class="fizz-text-large">
                Please connect distributors on the My Distributors page to enable Messages
              </div>
              <br />
              <a class="fizz-text-medium" href="/settings/accounts">Go to My Distributors</a>
            </span>
          </div>
        </div>
      {:else if currentConversation && currentMessages}
        <Conversation
          conversation={currentConversation}
          {searchQuery}
          {callbacks}
          messages={currentMessages}
          {goBackToPane}
        />
      {:else}
        <div class="grid-container-body padding-top--small margin-bottom--small fizz-full-height">
          <div class="grid-row align-center">
            <span class="grid-col empty-messages">
              {#if searchQuery}No messages matched your search of "{searchQuery}"
              {:else}
                <div class="secondary-body--bold padding-bottom-smaller">
                  You don't have any messages
                </div>
                <div class="secondary-body--regular">
                  Send order details, updates, or stay connected to your rep here
                </div>
              {/if}
            </span>
          </div>
        </div>
      {/if}
    </div>
  </div>
</div>
<NewMessageModal bind:showNewMessageModal {changeConversationWrapper} />
